import React, {useCallback, useEffect, useRef, useState} from 'react';
import {CircularProgress, Divider, IconButton, Stack, Typography} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import previewTheme from '../../previewPage/previewTheme';
import ReportContent from '../../previewPage/ReportContent';
import {callLLMStreaming} from '../../../../utils/llmStreamingApi';
import GeneratedContentContainer from './GeneratedContentContainer';
import {useReportStore} from '../../useReportStore';
import GenerationFormCollapsible from './GenerationFormCollapsible';
import {
    extractBlocksFromPMJson,
    getCountryCategory,
    getDisclosurePolicyDescription,
    normalizePmDoc
} from './generateInitialVersion/generationUtils';
import useGenerateInitialVersionStore from '../../../../stores/useGenerateInitialVersionStore';

/**
 * GenerateInitialVersionModal - Full-screen modal for reviewing generated disclosure version.
 * Displays the generated content before applying it to the editor.
 */
const GenerateInitialVersionModal = ({
                                         isOpen,
                                         onClose,
                                         parentContent,
                                         parentReportId,
                                         parentVersion,
                                         parentMeta,
                                         loading,
                                         error,
                                         instructions = '',
                                         selectedPolicy = 'default'
                                     }) => {
    // Get current disclosure document metadata from store
    const currentDocMeta = useReportStore(s => s.meta || {});
    const disclosureReportId = useReportStore(s => s.id);

    // Persisted generation state in global store (so it survives close/reopen)
    const generatedMarkdown = useGenerateInitialVersionStore(s => s.generatedMarkdown);
    const reasoningSteps = useGenerateInitialVersionStore(s => s.reasoningSteps);
    const isReasoning = useGenerateInitialVersionStore(s => s.isReasoning);
    const generatingError = useGenerateInitialVersionStore(s => s.generatingError);
    const isGenerating = useGenerateInitialVersionStore(s => s.isGenerating);
    const setGenerationState = useGenerateInitialVersionStore(s => s.setGenerationState);
    const startNewGenerationInStore = useGenerateInitialVersionStore(s => s.startNewGeneration);
    const resetGenerated = useGenerateInitialVersionStore(s => s.resetGenerated);

    // Keep AbortController in a ref so it survives re-renders; do NOT abort on close.
    const abortControllerRef = useRef(null);

    // Track the last reportId to detect changes
    const lastReportIdRef = useRef(parentReportId);
    const lastDisclosureIdRef = useRef(disclosureReportId);

    const [instructionsState, setInstructionsState] = useState(instructions);
    const [selectedPolicyState, setSelectedPolicyState] = useState(selectedPolicy);
    const [isFormExpanded, setIsFormExpanded] = useState(true);
    const [userToggledForm, setUserToggledForm] = useState(false);

    // Reset generated content when parent report or disclosure draft changes
    useEffect(() => {
        const parentChanged = parentReportId && lastReportIdRef.current !== parentReportId;
        const disclosureChanged = disclosureReportId && lastDisclosureIdRef.current !== disclosureReportId;

        if (parentChanged || disclosureChanged) {
            console.log('🔄 Disclosure generation context changed, resetting generated content:', {
                parentChanged,
                disclosureChanged,
                parentReportId,
                disclosureReportId,
            });
            resetGenerated();
            lastReportIdRef.current = parentReportId;
            lastDisclosureIdRef.current = disclosureReportId;
        }
    }, [parentReportId, disclosureReportId, resetGenerated]);

    // Clear stale pre-load errors once parent content is available
    useEffect(() => {
        if (!parentContent || loading) return;

        const message = generatingError?.message || '';
        if (message.includes('No parent report content available')
            || message.includes('No content available to generate')) {
            setGenerationState({generatingError: null});
        }
    }, [parentContent, loading, generatingError, setGenerationState]);

    // Close on Escape key
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen) {
            setInstructionsState(instructions || '');
            setSelectedPolicyState(selectedPolicy || 'default');
        }
    }, [isOpen, instructions, selectedPolicy]);

    useEffect(() => {
        if (isOpen) {
            setUserToggledForm(false);
            setIsFormExpanded(true);
        }
    }, [isOpen, parentReportId, parentVersion]);

    useEffect(() => {
        const shouldAutoHide = isGenerating || !!generatedMarkdown || !!generatingError;
        if (shouldAutoHide) {
            setIsFormExpanded(false);
            return;
        }
        if (!userToggledForm) {
            setIsFormExpanded(true);
        }
    }, [isGenerating, generatedMarkdown, generatingError, userToggledForm]);

    const handleAbort = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setGenerationState({
            isGenerating: false,
            isReasoning: false,
            generatingError: generatingError || new Error('Generation aborted'),
        });
    }, [setGenerationState, generatingError]);

    const startGeneration = useCallback(async () => {
        if (loading) return;
        if (error) return;
        if (!parentContent) {
            setGenerationState({generatingError: new Error('No parent report content available')});
            return;
        }

        const doc = normalizePmDoc(parentContent);
        const blocks = extractBlocksFromPMJson(doc || parentContent);
        if (blocks.length === 0) {
            setGenerationState({generatingError: new Error('No content available to generate.')});
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const generationId = startNewGenerationInStore();

        try {
            const promptId = 'pmpt_69788304094c8197b175ec847657413506a1be74423cfacf';

            const recipientCountry = currentDocMeta.selectedCountries || '';
            const recipientCategory = getCountryCategory(recipientCountry);
            const purposeValue = typeof currentDocMeta.purpose === 'object'
                ? currentDocMeta.purpose?.value || ''
                : currentDocMeta.purpose || '';
            const disclosurePolicy = getDisclosurePolicyDescription(selectedPolicyState);

            const inputData = {
                recipient_country: recipientCountry,
                recipient_category: recipientCategory,
                disclosure_purpose: purposeValue,
                disclosure_policy: disclosurePolicy,
                additional_analyst_instructions: instructionsState,
                content: blocks
            };

            const messages = [
                {
                    role: 'user',
                    content: JSON.stringify(inputData)
                }
            ];

            await callLLMStreaming(
                promptId,
                messages,
                (event) => {
                    // Ignore stale events if a new generation started

                    const currentId = useGenerateInitialVersionStore.getState().activeGenerationId;
                    if (currentId && generationId && currentId !== generationId) return;

                    switch (event.type) {
                        case 'started':
                            setGenerationState({isReasoning: true});
                            break;
                        case 'reasoning_started':
                            setGenerationState({
                                isReasoning: true,
                                reasoningSteps: event.reasoningSteps || [],
                            });
                            break;
                        case 'reasoning':
                            setGenerationState({
                                isReasoning: true,
                                reasoningSteps: event.reasoningSteps || [],
                            });
                            break;
                        case 'reasoning_done':
                            setGenerationState({
                                isReasoning: false,
                                reasoningSteps: event.reasoningSteps || [],
                            });
                            break;
                        case 'output':
                            setGenerationState({
                                isReasoning: false,
                                generatedMarkdown: event.outputText || '',
                                ...(event.reasoningSteps?.length ? {reasoningSteps: event.reasoningSteps} : {}),
                            });
                            break;
                        case 'output_done':
                        case 'completed':
                            setGenerationState({
                                isReasoning: false,
                                generatedMarkdown: event.outputText || '',
                                ...(event.reasoningSteps?.length ? {reasoningSteps: event.reasoningSteps} : {}),
                            });
                            break;
                        case 'error':
                            setGenerationState({
                                isReasoning: false,
                                generatingError: event.error || new Error('Generation failed'),
                            });
                            break;
                        case 'cancelled':
                            setGenerationState({
                                isReasoning: false,
                                generatingError: generatingError || new Error('Generation cancelled'),
                            });
                            break;
                        default:
                            break;
                    }
                },
                controller.signal
            );
        } catch (err) {
            if (err?.name === 'AbortError' || controller.signal.aborted) {
                setGenerationState({generatingError: generatingError || new Error('Generation aborted')});
                return;
            }
            setGenerationState({generatingError: err});
        } finally {
            setGenerationState({isGenerating: false, isReasoning: false});
        }
    }, [loading, error, parentContent, currentDocMeta, selectedPolicyState, instructionsState, setGenerationState, startNewGenerationInStore, generatingError]);

    const handleInstructionsChange = useCallback((event) => {
        setInstructionsState(event.target.value);
    }, []);

    const handlePolicyChange = useCallback((value) => {
        setSelectedPolicyState(value);
    }, []);

    const toggleForm = useCallback(() => {
        setUserToggledForm(true);
        setIsFormExpanded(prev => !prev);
    }, []);

    // NOTE: We intentionally do NOT reset generation state on close anymore.
    // Closing just hides the modal; generation/output stays in the store.

    if (!isOpen) return null;

    return (
        <Stack
            p={2}
            bgcolor="rgba(0,0,0,0.5)"
            position="fixed"
            top={0}
            left={0}
            width="100%"
            height="100vh"
            overflow="hidden"
            zIndex={1000}
        >
            <Stack
                height="100%"
                bgcolor="#fff"
                borderRadius={2}
                boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
                overflow="hidden"
            >


                {/* Content area */}
                <Stack flex={1} overflow="hidden">
                    <Stack direction={'row'} flexGrow={1} overflow={'hidden'}>
                        {/* Left side: Parent Report Content */}
                        <Stack width={'50%'} overflow={'hidden'} bgcolor={'grey.50'}>
                            <Stack
                                px={2}
                                py={1.5}
                                pr={1}
                                justifyContent="space-between"
                                alignItems="center"
                                direction="row"
                            >
                                <Typography variant="h6">Original report</Typography>
                            </Stack>

                            <Divider/>

                            <Stack px={4} overflow={'auto'} alignItems={'center'}>

                                {loading && (
                                    <Stack alignItems="center" justifyContent="center" py={4}>
                                        <CircularProgress/>
                                        <Typography variant="body2" color="text.secondary" mt={2}>
                                            Loading parent report...
                                        </Typography>
                                    </Stack>
                                )}
                                {error && (
                                    <Typography variant="body2" color="error">
                                        Error loading parent report: {error.message}
                                    </Typography>
                                )}
                                {!loading && !error && parentContent && (
                                    <ThemeProvider theme={previewTheme}>
                                        <CssBaseline/>
                                        <Stack className="preview-root" maxWidth={'800px'} width={'100%'}>
                                            <ReportContent
                                                content={parentContent}
                                                meta={parentMeta}
                                                reportDoc={{title: parentMeta?.title || 'Parent Report'}}
                                                version={parentVersion}
                                                latestVersion={parentVersion}
                                                // referencesByMarkId={{}}
                                                // referenceNumbers={{}}
                                                referencesByMarkId={new Map()}
                                                referenceNumbers={new Map()}
                                                getReferenceNumber={() => null}
                                                selectedAuthorId={null}
                                                onAuthorClick={() => {
                                                }}
                                            />
                                        </Stack>
                                    </ThemeProvider>
                                )}
                                {!loading && !error && !parentContent && (
                                    <Typography variant="body2" color="text.secondary">
                                        No parent report content available
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>

                        <Divider orientation={'vertical'}/>

                        {/* Right side: Generated Report */}
                        <Stack
                            width={'50%'}
                            overflow={'hidden'}>

                            <Stack
                                px={2}
                                py={1}
                                pr={1}
                                justifyContent="space-between"
                                alignItems="center"
                                direction="row"
                            >
                                <Typography variant="h6">Generate Initial Version</Typography>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <IconButton
                                        size="small"
                                        onClick={toggleForm}
                                        sx={{
                                            transform: isFormExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: theme => theme.transitions.create('transform'),
                                        }}
                                        aria-label={isFormExpanded ? 'Collapse generation form' : 'Expand generation form'}
                                    >
                                        <ExpandMoreIcon/>
                                    </IconButton>
                                    <IconButton onClick={onClose}>
                                        <CloseRoundedIcon/>
                                    </IconButton>
                                </Stack>
                            </Stack>

                            <GenerationFormCollapsible
                                instructions={instructionsState}
                                selectedPolicy={selectedPolicyState}
                                isGenerating={isGenerating}
                                isReasoning={isReasoning}
                                generatedMarkdown={generatedMarkdown}
                                generatingError={generatingError}
                                onInstructionsChange={handleInstructionsChange}
                                onPolicyChange={handlePolicyChange}
                                onGenerate={startGeneration}
                                onAbort={handleAbort}
                                expanded={isFormExpanded}
                                onExpandedChange={setIsFormExpanded}
                                generateDisabled={loading || !parentContent || !!error}
                            />

                            <Divider/>

                            <GeneratedContentContainer
                                markdown={generatedMarkdown}
                                error={generatingError}
                                isGenerating={isGenerating}
                                reasoningSteps={reasoningSteps}
                                isReasoning={isReasoning}
                                onReplace={onClose}
                            />

                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default GenerateInitialVersionModal;

